import { useQuery } from '@apollo/client';
import { Menu, Transition } from '@headlessui/react';
import {
  AcademicCapIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  MegaphoneIcon,
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { subDays } from 'date-fns';
import format from 'date-fns/format';
import { LeadNote, Messages, TeamMember } from 'interfaces';
import { isMissedCall } from 'lib/call-analytics';
import {
  type LeadCallContact,
  getCallActivityNotes,
  getLeadCallActivityDate,
  getLeadCallActivityDateFromRecord,
  getLeadCallActivityMeta,
  getLeadCallActivityTitle,
  resolveLeadActivityCall,
} from 'lib/lead-call-activity';
import type { LeadIncomingCall } from 'lib/lead-incoming-calls';
import type { MissiveLeadActivityItem } from 'lib/missive/lead-activity';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { Fragment, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import ActivityNoteCompose from 'components/activity/activity-note-compose';
import {
  type StudentActivityEvent,
  buildStudentActivityEvents,
} from 'components/activity/student-activity-events';
import useLeadIncomingCalls from 'components/leads/use-lead-incoming-calls';
import useLeadMissiveActivity from 'components/leads/use-lead-missive-activity';
import { useOpenMissiveEmail } from 'components/leads/use-open-missive-email';
import { useOpenMissiveWhatsApp } from 'components/leads/use-open-missive-whatsapp';

import classNames from 'utils/classnames';

import { getStudentActivityEvents } from 'queries/activity';
import { getClientNotes } from 'queries/clients';

import { OrganizationContextValue } from 'providers/organization';

import Chat from '../../assets/chat.svg';
import EmailOut from '../../assets/email-out.svg';

const TimeAgo = dynamic(
  // @ts-ignore
  () => import('react-timeago'),
  {
    ssr: false,
  },
);

const Modal = dynamic(() => import('components/molecules/modal'));

type ActivityItem =
  | {
      kind: 'note';
      id: string;
      date: Date;
      note: LeadNote;
      authorName?: string;
      avatarUrl?: string;
    }
  | {
      kind: 'message';
      id: string;
      date: Date;
      message: Messages;
    }
  | {
      kind: 'missive';
      id: string;
      date: Date;
      item: MissiveLeadActivityItem;
    }
  | {
      kind: 'call';
      id: string;
      date: Date;
      contact?: LeadCallContact;
      call?: LeadIncomingCall;
    }
  | {
      kind: 'event';
      id: string;
      date: Date;
      event: StudentActivityEvent;
    };

export type ActivityQuickActions = {
  onViewProfile: () => void;
  onReminder: () => void;
  reminderLabel: string;
  onSendAsset: () => void;
};

export type ActivityClient = {
  name?: string | null;
  surname?: string | null;
  email?: string | null;
};

export type ActivityPanelProps = {
  email?: string | null;
  phone?: string | null;
  userId?: string | null;
  leadId?: string | null;
  notes?: LeadNote[];
  messages?: Messages[];
  org: OrganizationContextValue;
  member?: TeamMember;
  client?: ActivityClient | null;
  quickActions?: ActivityQuickActions;
  onOpenCall?: (callId: string) => void;
  onOpenPayment?: () => void;
};

function activityTimestamp(date: Date) {
  const time = date instanceof Date ? date.getTime() : Number.NaN;
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

function missiveActivityDate(deliveredAt: number) {
  if (!Number.isFinite(deliveredAt) || deliveredAt <= 0) {
    return new Date(0);
  }

  const milliseconds = deliveredAt > 1e12 ? deliveredAt : deliveredAt * 1000;
  return new Date(milliseconds);
}

function mergeActivity({
  notes,
  studentNotes,
  messages,
  missiveItems,
  incomingCalls,
  events,
}: {
  notes: LeadNote[] | undefined;
  studentNotes: LeadNote[] | undefined;
  messages: Messages[] | undefined;
  missiveItems: MissiveLeadActivityItem[] | undefined;
  incomingCalls: LeadIncomingCall[] | undefined;
  events: StudentActivityEvent[];
}): ActivityItem[] {
  const items: ActivityItem[] = [];
  const seenNoteIds = new Set<string>();

  const pushNote = (note: LeadNote, prefix: string) => {
    const noteId = note.data?.id || note.id;
    if (seenNoteIds.has(noteId)) return;
    seenNoteIds.add(noteId);
    items.push({
      kind: 'note',
      id: `${prefix}-${note.id}`,
      date: new Date(note.data.updatedAt || note.data.createdAt),
      note,
    });
  };

  notes?.forEach((note) => pushNote(note, 'note'));
  studentNotes?.forEach((note) => pushNote(note, 'student-note'));

  messages?.forEach((message) => {
    if (message.type === 'call') {
      items.push({
        kind: 'call',
        id: `call-${message.id}`,
        date: getLeadCallActivityDate(message as LeadCallContact),
        contact: message as LeadCallContact,
      });
      return;
    }

    items.push({
      kind: 'message',
      id: `message-${message.id}`,
      date: new Date(message.updatedAt || message.createdAt),
      message,
    });
  });

  const linkedCallIds = new Set(
    (messages || [])
      .filter((message) => message.type === 'call')
      .map((message) => message.call_id || message.call?.id)
      .filter(Boolean),
  );

  incomingCalls?.forEach((call) => {
    if (linkedCallIds.has(call.id)) {
      return;
    }

    items.push({
      kind: 'call',
      id: `incoming-call-${call.id}`,
      date: getLeadCallActivityDateFromRecord(call),
      call,
    });
  });

  missiveItems?.forEach((item) => {
    if (!item.deliveredAt) {
      return;
    }

    items.push({
      kind: 'missive',
      id: item.id,
      date: missiveActivityDate(item.deliveredAt),
      item,
    });
  });

  events.forEach((event) => {
    items.push({
      kind: 'event',
      id: event.id,
      date: event.date,
      event,
    });
  });

  return items.sort(
    (a, b) => activityTimestamp(b.date) - activityTimestamp(a.date),
  );
}

function missiveChannelLabel(channel: string) {
  switch (channel) {
    case 'email':
      return 'Email';
    case 'whatsapp':
      return 'WhatsApp';
    case 'sms':
      return 'SMS';
    case 'social':
      return 'Social';
    default:
      return channel;
  }
}

function messageLabel(message: Messages) {
  if (message.source === 'application' && message.type === 'sms') {
    return 'Outbound SMS';
  }
  if (message.source === 'application' && message.type === 'email') {
    return 'Outbound email';
  }
  if (message.source === 'application' && message.type === 'whatsapp') {
    return message.message?.trim() || 'WhatsApp';
  }
  if (message.source === 'application' && message.type === 'missive') {
    return message.message?.trim() || 'Missive';
  }
  if (message.source === 'client' && message.type === 'sms') {
    return 'Inbound SMS';
  }
  if (message.source === 'client' && message.type === 'email') {
    return 'Inbound email';
  }
  if (message.source === 'client' && message.type === 'whatsapp') {
    return 'Inbound WhatsApp';
  }
  return message.type;
}

function OpenMissiveLink({
  label = 'Open in Missive',
  onClick,
}: {
  label?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
    >
      {label}
    </button>
  );
}

function eventIcon(type: StudentActivityEvent['type']) {
  switch (type) {
    case 'certificate':
      return <AcademicCapIcon className="h-4 w-4 text-purple-600" />;
    case 'payment':
      return <BanknotesIcon className="h-4 w-4 text-emerald-600" />;
    case 'confirmation':
      return <CheckCircleIcon className="h-4 w-4 text-purple-600" />;
    case 'joined':
      return <UserIcon className="h-4 w-4 text-gray-600" />;
    default:
      return <ComputerDesktopIcon className="h-4 w-4 text-gray-600" />;
  }
}

async function openCertificate(url?: string) {
  if (!url) return;
  try {
    const part = url.split('/private')[1];
    if (!part) {
      window.open(url, '_blank');
      return;
    }
    const req = await fetch(`/api/link?key=${part}`);
    const { url: signedUrl } = await req.json();
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  } catch (err) {
    console.log(err);
  }
}

function mapStudentNotes(notes: any[] | undefined): LeadNote[] {
  return (notes || [])
    .map((item) => {
      const note = item.note;
      if (!note) return null;
      return {
        id: item.id,
        data: {
          id: note.id,
          title: note.title,
          content: note.content,
          user: note.employee?.id,
          createdAt: note.created_at || item.created_at,
          updatedAt: note.created_at || item.created_at,
        },
      } as LeadNote;
    })
    .filter(Boolean) as LeadNote[];
}

export default function ActivityPanel({
  email,
  phone,
  userId,
  leadId,
  notes,
  messages,
  org,
  client,
  quickActions,
  onOpenCall,
  onOpenPayment,
}: ActivityPanelProps) {
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const { openMissiveWhatsApp, WhatsAppComposeModal } =
    useOpenMissiveWhatsApp();
  const { openMissiveEmail, EmailComposeModal } = useOpenMissiveEmail();
  const recipientName = [client?.name, client?.surname]
    .filter(Boolean)
    .join(' ');
  const canAddNote = Boolean(leadId || userId);
  const handleOpenEmail = () => {
    if (!email?.trim()) {
      return;
    }

    openMissiveEmail(email.trim(), {
      leadId: leadId || undefined,
      name: recipientName || undefined,
    });
  };
  const {
    data: missiveActivity,
    loading: missiveLoading,
    error: missiveError,
  } = useLeadMissiveActivity({ email, phone });

  const linkedCallIds = useMemo(
    () =>
      (messages || [])
        .filter((message) => message.type === 'call')
        .map((message) => message.call_id || message.call?.id)
        .filter(Boolean) as string[],
    [messages],
  );

  const {
    data: incomingCallActivity,
    loading: incomingCallsLoading,
    error: incomingCallsError,
  } = useLeadIncomingCalls({
    phone,
    userId,
    excludeIds: linkedCallIds,
  });

  const { data: studentEventsData, loading: studentEventsLoading } = useQuery(
    getStudentActivityEvents,
    {
      variables: { userId },
      skip: !userId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: studentNotesData } = useQuery(getClientNotes, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const studentEvents = useMemo(
    () => buildStudentActivityEvents(studentEventsData, recipientName),
    [studentEventsData, recipientName],
  );

  const studentNotes = useMemo(
    () => mapStudentNotes(studentNotesData?.user_note),
    [studentNotesData?.user_note],
  );

  const activity = useMemo(
    () =>
      mergeActivity({
        notes,
        studentNotes,
        messages,
        missiveItems: missiveActivity?.items,
        incomingCalls: incomingCallActivity?.items,
        events: studentEvents,
      }),
    [
      notes,
      studentNotes,
      messages,
      missiveActivity?.items,
      incomingCallActivity?.items,
      studentEvents,
    ],
  );

  const NoteParagraph = ({ ...props }) => (
    <p className="mb-1 font-light leading-relaxed last:mb-0" {...props} />
  );

  const loadingActivity =
    (missiveLoading || incomingCallsLoading || studentEventsLoading) &&
    activity.length === 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-gray-900">Activity</h2>
              <p className="text-xs text-gray-500">
                Internal notes, calls, app messages, Missive conversations, and
                student history
              </p>
              {missiveActivity?.configured ? (
                <p className="mt-1 text-[11px] text-teal-700">
                  {missiveLoading
                    ? 'Loading Missive…'
                    : `${missiveActivity.conversations.length} Missive conversation${
                        missiveActivity.conversations.length === 1 ? '' : 's'
                      }`}
                </p>
              ) : null}
              {missiveError ? (
                <p className="mt-1 text-[11px] text-amber-700">
                  {missiveError}
                </p>
              ) : null}
              {incomingCallsLoading && (phone?.trim() || userId?.trim()) ? (
                <p className="mt-1 text-[11px] text-gray-500">Loading calls…</p>
              ) : incomingCallActivity?.items.length ? (
                <p className="mt-1 text-[11px] text-gray-600">
                  {incomingCallActivity.items.length} matched call
                  {incomingCallActivity.items.length === 1 ? '' : 's'}
                </p>
              ) : null}
              {incomingCallsError ? (
                <p className="mt-1 text-[11px] text-amber-700">
                  {incomingCallsError}
                </p>
              ) : null}
              {phone?.trim() ? (
                <OpenMissiveLink
                  label="Open WhatsApp in Missive"
                  onClick={() =>
                    openMissiveWhatsApp(phone.trim(), {
                      leadId: leadId || undefined,
                      name: recipientName || undefined,
                    })
                  }
                />
              ) : email?.trim() ? (
                <OpenMissiveLink
                  label="Open email in Missive"
                  onClick={handleOpenEmail}
                />
              ) : null}
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {canAddNote ? (
                <button
                  type="button"
                  onClick={() => setNoteModalOpen(true)}
                  className="inline-flex items-center rounded-md border border-purple-200 bg-purple-50 px-2.5 py-1.5 text-xs font-medium text-purple-700 shadow-sm hover:bg-purple-100"
                >
                  Add note
                </button>
              ) : null}
              {quickActions ? (
                <Menu as="div" className="relative flex-shrink-0">
                  <Menu.Button className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                    Quick actions
                    <ChevronDownIcon
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={quickActions.onViewProfile}
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block w-full px-3 py-2 text-left text-sm text-gray-700',
                            )}
                          >
                            View profile
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={quickActions.onReminder}
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block w-full px-3 py-2 text-left text-sm text-gray-700',
                            )}
                          >
                            {quickActions.reminderLabel}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={quickActions.onSendAsset}
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block w-full px-3 py-2 text-left text-sm text-gray-700',
                            )}
                          >
                            Send asset
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              ) : null}
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-4">
          {loadingActivity ? (
            <p className="text-sm text-gray-500">Loading activity…</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-500">No activity yet.</p>
          ) : (
            <ul className="space-y-4">
              {activity.map((item) => {
                const showAbsoluteDate = item.date <= subDays(new Date(), 2);

                if (item.kind === 'event') {
                  const event = item.event;
                  const isPayment = event.type === 'payment';
                  const isCertificate = event.type === 'certificate';

                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-gray-100 bg-gray-50/70 p-3"
                    >
                      <div className="flex gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white">
                          {eventIcon(event.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                              {event.label}
                            </span>
                            <time
                              dateTime={item.date.toISOString()}
                              className="flex-shrink-0 text-[10px] text-gray-500"
                            >
                              {showAbsoluteDate ? (
                                format(item.date, 'do MMM yyyy, HH:mm')
                              ) : (
                                <TimeAgo live date={item.date.toISOString()} />
                              )}
                            </time>
                          </div>
                          <p className="mt-1 text-xs text-gray-800">
                            {event.title}
                          </p>
                          {event.href ? (
                            <a
                              href={event.href}
                              target={
                                event.href.startsWith('http')
                                  ? '_blank'
                                  : undefined
                              }
                              rel={
                                event.href.startsWith('http')
                                  ? 'noreferrer'
                                  : undefined
                              }
                              className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
                            >
                              View course
                            </a>
                          ) : null}
                          {isPayment && onOpenPayment ? (
                            <button
                              type="button"
                              onClick={onOpenPayment}
                              className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
                            >
                              View payments
                            </button>
                          ) : null}
                          {isCertificate && event.certificateUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                                openCertificate(event.certificateUrl)
                              }
                              className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
                            >
                              View certificate
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                }

                if (item.kind === 'missive') {
                  const missiveItem = item.item;

                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-teal-100 bg-teal-50/40 p-3"
                    >
                      <div className="flex gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-[10px] font-bold text-teal-800">
                          M
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-800">
                                Missive ·{' '}
                                {missiveChannelLabel(missiveItem.channel)} ·{' '}
                                {missiveItem.teamLabel}
                              </span>
                              <p className="text-xs font-semibold text-gray-900">
                                {missiveItem.author || 'Conversation'}
                              </p>
                            </div>
                            <time
                              dateTime={item.date.toISOString()}
                              className="flex-shrink-0 text-[10px] text-gray-500"
                            >
                              {showAbsoluteDate ? (
                                format(item.date, 'do MMM yyyy, HH:mm')
                              ) : (
                                <TimeAgo live date={item.date.toISOString()} />
                              )}
                            </time>
                          </div>
                          {missiveItem.subject ? (
                            <p className="mt-1 text-xs font-medium text-gray-800">
                              {missiveItem.subject}
                            </p>
                          ) : null}
                          <p className="mt-1 text-xs text-gray-700">
                            {missiveItem.preview}
                          </p>
                          {missiveItem.webUrl ? (
                            <a
                              href={missiveItem.webUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
                            >
                              Open in Missive
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                }

                if (item.kind === 'call') {
                  const callRecord = resolveLeadActivityCall(item);
                  const callId = callRecord?.id || item.contact?.call_id;
                  const missed = callRecord ? isMissedCall(callRecord) : false;
                  const title = callRecord
                    ? getLeadCallActivityTitle(callRecord)
                    : 'Call';
                  const meta = callRecord
                    ? getLeadCallActivityMeta(callRecord)
                    : null;
                  const author = item.contact?.user;
                  const borderClass = missed
                    ? 'border-red-100 bg-red-50/50'
                    : callRecord?.type === 'outgoing'
                      ? 'border-green-100 bg-green-50/40'
                      : 'border-orange-100 bg-orange-50/40';

                  return (
                    <li
                      key={item.id}
                      className={classNames(
                        'rounded-lg border p-3',
                        borderClass,
                      )}
                    >
                      <div className="flex gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                          {missed && callRecord?.voicemail ? (
                            <MegaphoneIcon className="h-5 w-5 text-red-600" />
                          ) : missed ? (
                            <PhoneXMarkIcon className="h-5 w-5 text-red-600" />
                          ) : callRecord?.type === 'outgoing' ? (
                            <PhoneArrowUpRightIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <PhoneArrowDownLeftIcon className="h-5 w-5 text-orange-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                                Call
                              </span>
                              <p className="text-xs font-semibold text-gray-900">
                                {title}
                              </p>
                              {author?.name ? (
                                <p className="text-[11px] text-gray-600">
                                  {[author.name, author.surname]
                                    .filter(Boolean)
                                    .join(' ')}
                                </p>
                              ) : null}
                            </div>
                            <time
                              dateTime={item.date.toISOString()}
                              className="flex-shrink-0 text-[10px] text-gray-500"
                            >
                              {showAbsoluteDate ? (
                                format(item.date, 'do MMM yyyy, HH:mm')
                              ) : (
                                <TimeAgo live date={item.date.toISOString()} />
                              )}
                            </time>
                          </div>
                          {meta ? (
                            <p className="mt-1 text-xs text-gray-600">{meta}</p>
                          ) : null}
                          {getCallActivityNotes(callRecord).map((note) => (
                            <div
                              key={note.id}
                              className="mt-2 text-xs text-gray-700"
                            >
                              <ReactMarkdown components={{ p: NoteParagraph }}>
                                {note.content}
                              </ReactMarkdown>
                            </div>
                          ))}
                          {callId && onOpenCall ? (
                            <button
                              type="button"
                              onClick={() => onOpenCall(callId)}
                              className="mt-2 inline-flex text-xs font-semibold text-teal-800 underline hover:text-teal-900"
                            >
                              Open call
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                }

                if (item.kind === 'note') {
                  const note = item.note;
                  const author = org.team?.find(
                    (teamMember) => teamMember.user.id === note.data.user,
                  );
                  const avatar =
                    author?.user.avatar && author.user.avatar.length > 0
                      ? author.user.avatar[0].asset
                      : null;

                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-gray-100 bg-gray-50/60 p-3"
                    >
                      <div className="flex gap-2">
                        {avatar ? (
                          <div className="relative h-7 w-7 flex-shrink-0">
                            <Image
                              layout="fill"
                              className="rounded-full"
                              src={avatar.url}
                              alt={author?.user.name || 'Team member'}
                            />
                          </div>
                        ) : (
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-[10px] font-semibold text-purple-700">
                            {author?.user.name?.charAt(0)}
                            {author?.user.surname?.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                                Note
                              </span>
                              <p className="text-xs font-semibold text-gray-900">
                                {author?.user.name || 'Internal note'}
                              </p>
                            </div>
                            <time
                              dateTime={item.date.toISOString()}
                              className="flex-shrink-0 text-[10px] text-gray-500"
                            >
                              {showAbsoluteDate ? (
                                format(item.date, 'do MMM yyyy, HH:mm')
                              ) : (
                                <TimeAgo live date={item.date.toISOString()} />
                              )}
                            </time>
                          </div>
                          <div className="mt-1 text-xs text-gray-700">
                            <ReactMarkdown components={{ p: NoteParagraph }}>
                              {note.data.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }

                const message = item.message;
                const author = org.team?.find(
                  (teamMember) => teamMember.user.id === message.user?.id,
                );
                const avatar =
                  author?.user.avatar && author.user.avatar.length > 0
                    ? author.user.avatar[0].asset
                    : null;

                return (
                  <li
                    key={item.id}
                    className="rounded-lg border border-gray-100 bg-white p-3"
                  >
                    <div className="flex gap-2">
                      {avatar ? (
                        <div className="relative h-7 w-7 flex-shrink-0">
                          <Image
                            layout="fill"
                            className="rounded-full"
                            src={avatar.url}
                            alt={author?.user.name || 'Team member'}
                          />
                        </div>
                      ) : (
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-600">
                          {author?.user.name?.charAt(0)}
                          {author?.user.surname?.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-teal-700">
                              {message.type === 'email' ? (
                                <EmailOut className="inline h-3 w-3" />
                              ) : (
                                <Chat className="inline h-3 w-3" />
                              )}
                              {messageLabel(message)}
                            </span>
                            <p className="text-xs font-semibold text-gray-900">
                              {author?.user.name}
                            </p>
                          </div>
                          <time
                            dateTime={item.date.toISOString()}
                            className="flex-shrink-0 text-[10px] text-gray-500"
                          >
                            {showAbsoluteDate ? (
                              format(item.date, 'do MMM yyyy, HH:mm')
                            ) : (
                              <TimeAgo live date={item.date.toISOString()} />
                            )}
                          </time>
                        </div>
                        {message.type === 'email' && message.subject ? (
                          <p className="mt-1 text-xs font-medium text-gray-800">
                            {message.subject}
                          </p>
                        ) : null}
                        {message.message ? (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-gray-600">
                            {message.message}
                          </p>
                        ) : null}
                        {message.type === 'whatsapp' && phone?.trim() ? (
                          <OpenMissiveLink
                            onClick={() =>
                              openMissiveWhatsApp(phone.trim(), {
                                leadId: leadId || undefined,
                                name: recipientName || undefined,
                              })
                            }
                          />
                        ) : null}
                        {message.type === 'email' && email?.trim() ? (
                          <OpenMissiveLink onClick={handleOpenEmail} />
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {noteModalOpen && canAddNote ? (
        <Modal
          open={noteModalOpen}
          small
          title="Add note"
          subTitle="Internal note - visible in activity feed"
          close={() => setNoteModalOpen(false)}
        >
          <div className="px-4 py-4">
            <ActivityNoteCompose
              leadId={leadId}
              userId={userId}
              showHeading={false}
              onSaved={() => setNoteModalOpen(false)}
              onCancel={() => setNoteModalOpen(false)}
            />
          </div>
        </Modal>
      ) : null}
      {WhatsAppComposeModal}
      {EmailComposeModal}
    </div>
  );
}
